import { Box, Typography, getTokenCssVar } from '@vp/swan';

export type AnsweredTileProps = {
    imageUrl: string;
    name: string;
    /**
     * Image height in px. Each descriptor owns its own answered-tile geometry — legacy's
     * TileCard took the same per-caller knob, e.g. style-tiles' single square-ish pick at 80 vs.
     * color-selector's shorter swatch strip at 50 — so this stays a prop rather than a constant
     * baked into the shared card.
     *
     * @default 80
     */
    imageHeight?: number;
    /**
     * Matches legacy TileCard's `imageObjectFit`: style-tiles crops a photo to `cover`,
     * color-selector stretches a flat swatch to `fill` so its color bands aren't clipped.
     *
     * @default 'cover'
     */
    imageObjectFit?: 'cover' | 'fill';
};

/** Legacy's answered-tile width, which the pilot ships in production for every choice type. */
const CARD_WIDTH = '120px';
const DEFAULT_IMAGE_HEIGHT = 80;
const DEFAULT_IMAGE_OBJECT_FIT = 'cover';

/**
 * The settled, read-only answer for a single generative-UI choice: the option the customer
 * picked (or one of several, for a multi-select type), shown inside their own message bubble.
 *
 * Shared by every descriptor's `MessageView` — style-tiles and color-selector both render this
 * exact card, so any future single-choice or multi-choice answered type gets the pilot's look
 * for free instead of re-deriving it. `imageHeight`/`imageObjectFit` are the two dimensions
 * legacy varied per type; everything else about the card is fixed.
 *
 * Deliberately not a SWAN `StandardTile skin="product"`. That tile is a product-listing
 * component — it sizes itself from its container and styles its own name row, which made the
 * answered bubble render at a different width and type scale than the pilot. This is a fixed
 * 120px card instead, matching legacy TileCard exactly, because the bubble is a confirmation of
 * a past choice rather than a browsable product.
 *
 * No border, for the same reason ChatInterface's composer band has none: legacy asks for
 * `1px solid var(--swan-sem-color-border-subtle)`, but SWAN never defines that custom property,
 * so the declaration is invalid and dropped and no border has ever rendered in production.
 */
export function AnsweredTile({
    imageUrl,
    name,
    imageHeight = DEFAULT_IMAGE_HEIGHT,
    imageObjectFit = DEFAULT_IMAGE_OBJECT_FIT,
}: AnsweredTileProps) {
    return (
        <Box
            backgroundColor="standard"
            style={{
                width: CARD_WIDTH,
                borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                // Clips the image's square corners to the card's rounded ones.
                overflow: 'hidden',
            }}
        >
            <img
                src={imageUrl}
                alt={name}
                style={{
                    width: '100%',
                    height: `${imageHeight}px`,
                    objectFit: imageObjectFit,
                    // Kills the inline-element baseline gap under the image.
                    display: 'block',
                }}
            />
            <Box paddingY={2} paddingX={3}>
                <Typography fontSkin="body-small-bold" style={{ textAlign: 'center' }}>
                    {name}
                </Typography>
            </Box>
        </Box>
    );
}
